import { useEffect, useMemo, useState } from 'react';
import appTranslations from './locales/app';
import memberTranslations from './locales/member';
import adminTranslations from './locales/admin';
import superAdminTranslations from './locales/superAdmin';
import legalTranslations from './locales/legal';

export const SUPPORTED_LANGUAGES = ['fi', 'en', 'sv'];
const STORAGE_KEY = 'masaklubi_language';
const LANGUAGE_CHANGE_EVENT = 'masaklubi-language-change';

const baseTranslations = {
  fi: {
    language: 'Kieli',
    nav: {
      club: 'Klubi',
      viewingClub: 'Katselet klubia:',
      backToSuperAdmin: 'Takaisin SuperAdminiin',
      home: 'Etusivu',
      games: 'Pelit',
      events: 'Peli-illat',
      analytics: 'Analyysit',
      suggestions: 'Kehityslaatikko',
      admin: 'Admin',
      superAdmin: 'Super Admin',
      clubDescription: 'Klubin kuvaus',
      logout: 'Kirjaudu ulos'
    },
    landing: {
      latestVersion: 'Uusin versio',
      heading: 'Lautapeliklubisi ansaitsee',
      headingAccent: ' paremman alustan',
      subtitle: 'Lopeta WhatsApp-säätö ja Excel-taulukot. Hallitse liigaa, sovi peliajat ja seuraa tilastoja yhdessä paikassa.',
      invalidEmail: 'Anna toimiva sähköpostiosoite',
      genericError: 'Jokin meni pieleen',
      connectionError: 'Yhteysvirhe. Yritä uudelleen.',
      emailPlaceholder: 'anna@esimerkki.fi',
      startFree: 'Aloita ilmaiseksi',
      checkEmail: 'Tarkista sähköpostisi! Lähetimme sinulle vahvistuskoodin.',
      freeUse: 'Ilmainen käyttö',
      noCard: 'Ei luottokorttia',
      readyFast: 'Valmis 2 minuutissa',
      clubPreview: 'Peliklubi',
      featuresHeading: 'Kaikki mitä tarvitset lautapeliliigan pyörittämiseen',
      features: [
        ['Reaaliaikainen ranking', 'Dynaaminen pistejärjestelmä, joka huomioi pelaajamäärän ja vastustajien tason.'],
        ['Doodle-tyylinen aikataulutus', 'Ehdota aikoja, äänestä sopivista ja saa AI-suosituksia historian perusteella.'],
        ['Kattavat tilastot', '13 eri tilastokategoriaa: voittoprosentit, peliajat, ennätykset ja paljon muuta.'],
        ['Pelaajahallinta', 'Kutsu jäseniä magic link -kirjautumisella. Ei salasanoja muistettavaksi.'],
        ['BGG-integraatio', 'Hae pelitiedot automaattisesti BoardGameGeekistä. Kuvat, säännöt ja arvostelut.'],
        ['AI-avustaja', 'Kysy pelisääntöjä, pyydä setup-ohjeita tai analysoi tilastoja keskustelemalla.']
      ],
      quote: '"Ennen käytettiin WhatsAppia ja Exceliä. Nyt kaikki on yhdessä paikassa ja peli-iltojen sopiminen on helppoa."',
      quoteAuthor: '— Pelaaja, Peliklubi',
      ctaHeading: 'Valmis aloittamaan?',
      ctaText: 'Luo oma klubisi ilmaiseksi muutamassa minuutissa.',
      createClub: 'Luo klubi nyt',
      featuresLink: 'Ominaisuudet',
      pricingLink: 'Hinnoittelu',
      contactLink: 'Yhteystiedot',
      copyright: 'Kaikki oikeudet pidätetään.'
    }
  },
  en: {
    language: 'Language',
    nav: {
      club: 'Club',
      viewingClub: 'Viewing club:',
      backToSuperAdmin: 'Back to SuperAdmin',
      home: 'Home',
      games: 'Games',
      events: 'Game nights',
      analytics: 'Analytics',
      suggestions: 'Suggestion box',
      admin: 'Admin',
      superAdmin: 'Super Admin',
      clubDescription: 'Club description',
      logout: 'Log out'
    },
    landing: {
      latestVersion: 'Latest version',
      heading: 'Your board game club deserves',
      headingAccent: ' a better platform',
      subtitle: 'Leave WhatsApp chaos and spreadsheets behind. Manage your league, schedule games and follow statistics in one place.',
      invalidEmail: 'Enter a valid email address',
      genericError: 'Something went wrong',
      connectionError: 'Connection error. Please try again.',
      emailPlaceholder: 'you@example.com',
      startFree: 'Start for free',
      checkEmail: 'Check your email! We sent you a verification code.',
      freeUse: 'Free to use',
      noCard: 'No credit card',
      readyFast: 'Ready in 2 minutes',
      clubPreview: 'Game club',
      featuresHeading: 'Everything you need to run a board game league',
      features: [
        ['Live rankings', 'A dynamic scoring system that accounts for player count and opponent strength.'],
        ['Easy scheduling', 'Suggest times, vote for suitable options and get AI recommendations based on history.'],
        ['Comprehensive statistics', '13 statistical categories including win rates, play times and records.'],
        ['Player management', 'Invite members with magic links. No passwords to remember.'],
        ['BGG integration', 'Fetch game information automatically from BoardGameGeek: images, rules and ratings.'],
        ['AI assistant', 'Ask about rules, request setup instructions or analyse statistics conversationally.']
      ],
      quote: '"We used to rely on WhatsApp and Excel. Now everything is in one place and scheduling game nights is easy."',
      quoteAuthor: '— Player, Game club',
      ctaHeading: 'Ready to get started?',
      ctaText: 'Create your club for free in just a few minutes.',
      createClub: 'Create a club',
      featuresLink: 'Features',
      pricingLink: 'Pricing',
      contactLink: 'Contact',
      copyright: 'All rights reserved.'
    }
  },
  sv: {
    language: 'Språk',
    nav: {
      club: 'Klubb',
      viewingClub: 'Du visar klubben:',
      backToSuperAdmin: 'Tillbaka till SuperAdmin',
      home: 'Startsida',
      games: 'Spel',
      events: 'Spelkvällar',
      analytics: 'Analyser',
      suggestions: 'Förslagslåda',
      admin: 'Admin',
      superAdmin: 'Super Admin',
      clubDescription: 'Klubbeskrivning',
      logout: 'Logga ut'
    },
    landing: {
      latestVersion: 'Senaste versionen',
      heading: 'Din brädspelsklubb förtjänar',
      headingAccent: ' en bättre plattform',
      subtitle: 'Lämna WhatsApp-röran och kalkylbladen bakom dig. Hantera ligan, planera speltider och följ statistik på ett ställe.',
      invalidEmail: 'Ange en giltig e-postadress',
      genericError: 'Något gick fel',
      connectionError: 'Anslutningsfel. Försök igen.',
      emailPlaceholder: 'du@exempel.se',
      startFree: 'Börja gratis',
      checkEmail: 'Kontrollera din e-post! Vi skickade en verifieringskod.',
      freeUse: 'Gratis att använda',
      noCard: 'Inget kreditkort',
      readyFast: 'Klar på 2 minuter',
      clubPreview: 'Spelklubb',
      featuresHeading: 'Allt du behöver för att driva en brädspelsliga',
      features: [
        ['Ranking i realtid', 'Ett dynamiskt poängsystem som tar hänsyn till spelarantal och motståndarnas nivå.'],
        ['Enkel schemaläggning', 'Föreslå tider, rösta och få AI-rekommendationer utifrån historiken.'],
        ['Omfattande statistik', '13 statistikkategorier med bland annat vinstprocent, speltider och rekord.'],
        ['Spelarhantering', 'Bjud in medlemmar med magic links. Inga lösenord att komma ihåg.'],
        ['BGG-integration', 'Hämta spelinformation automatiskt från BoardGameGeek: bilder, regler och betyg.'],
        ['AI-assistent', 'Fråga om regler, be om setup-instruktioner eller analysera statistik i en dialog.']
      ],
      quote: '"Tidigare använde vi WhatsApp och Excel. Nu finns allt på ett ställe och det är enkelt att planera spelkvällar."',
      quoteAuthor: '— Spelare, Spelklubb',
      ctaHeading: 'Redo att börja?',
      ctaText: 'Skapa din klubb gratis på bara några minuter.',
      createClub: 'Skapa en klubb',
      featuresLink: 'Funktioner',
      pricingLink: 'Priser',
      contactLink: 'Kontakt',
      copyright: 'Alla rättigheter förbehållna.'
    }
  }
};

function deepMerge(target, source) {
  const result = { ...target };
  Object.entries(source || {}).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = deepMerge(result[key] || {}, value);
    } else {
      result[key] = value;
    }
  });
  return result;
}

const translationBundles = [
  appTranslations,
  memberTranslations,
  adminTranslations,
  superAdminTranslations,
  legalTranslations
];

export const translations = SUPPORTED_LANGUAGES.reduce((all, language) => {
  all[language] = translationBundles.reduce(
    (merged, bundle) => deepMerge(merged, bundle?.[language] || {}),
    baseTranslations[language]
  );
  return all;
}, {});

export function normalizeLanguage(value) {
  const language = String(value || '').toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(language) ? language : 'fi';
}

function initialLanguage() {
  return normalizeLanguage(localStorage.getItem(STORAGE_KEY) || navigator.language);
}

export function useI18n(preferredLanguage) {
  const [language, setLanguageState] = useState(() =>
    preferredLanguage ? normalizeLanguage(preferredLanguage) : initialLanguage()
  );

  useEffect(() => {
    if (preferredLanguage) setLanguageState(normalizeLanguage(preferredLanguage));
  }, [preferredLanguage]);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      setLanguageState(normalizeLanguage(event.detail));
    };
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    return () => window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem(STORAGE_KEY, language);
    window.dispatchEvent(new CustomEvent(LANGUAGE_CHANGE_EVENT, { detail: language }));
  }, [language]);

  const setLanguage = (nextLanguage) => setLanguageState(normalizeLanguage(nextLanguage));
  const t = useMemo(() => translations[language] || translations.fi, [language]);

  return { language, setLanguage, t };
}
